import Box from "../Box";

import styled from "styled-components";
import { CText } from "@carrefour/design-system-components-react";
import BaseDashboardCard from "../BaseDashboardCard";
import { DEAVA } from "../../types/operational";
import { useSearchParams } from "react-router-dom";
import { SingleValue } from "react-select";
import { Choice } from "../../types/commercial";
import SelectOption from "../SelectOption";
import useBreakPoint from "../../utils/useBreakPoint";
import { eventTrack } from "../../utils/gtag";
import { useTranslation } from "react-i18next";
import { formatPercentage } from "../../utils/numberUtils";
import { useRange } from "../../context/rangeContext";
import { Range } from "../../utils/range";

const Text = styled(CText)<{ color?: string }>`
  color: ${(props) => props.color};
  font-family: system-ui;
`;

type DeavaCardProps = {
  title: string;
  deavaData: DEAVA;
  isUnavailable: boolean;
  range: string;
  options: Choice[];
  messageError?: string;
  isLoading: boolean;
  openInformationModal: (title?: string) => void;
};

const BoxCustom = styled(Box)<{ gap: string }>`
  gap: ${(props) => props.gap};
  display: flex;
`;
const SelectBox = styled(Box)`
  width: 60%;
  @media only screen and (max-width: 600px) {
    width: 100%;
  }
`;

const getColorText = (value: number): string => {
  if (value === -1) return "black";

  if (value > 97) {
    return "#33691E";
  } else if (value <= 97 && value > 94) {
    return "#CC4010";
  }

  return "#AA0D11";
};

const computePercentage = (
  numerator?: number | null,
  denominator?: number | null
): number => {
  if (numerator == null || denominator == null || denominator === 0) {
    return 0;
  }

  const value = (numerator / denominator) * 100;
  return Number.isFinite(value) ? value : 0;
};

const safeCount = (value?: number | null): number => {
  if (value == null || !Number.isFinite(value)) {
    return 0;
  }

  return value;
};

const DeavaCard = ({
  title,
  deavaData,
  range,
  options,
  ...res
}: DeavaCardProps) => {
  const isBreak = useBreakPoint();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getKpiRange, setKpiRange } = useRange();
 
  const getDefaultValueParams = () => {
    const value = getKpiRange(range) ?? options?.[0]?.value as string;
    searchParams.set(range, value);
    setSearchParams(searchParams);
    // tracking first
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: value,
    });
    return value;
  };

  const rangeParam = searchParams.get(range) ?? getDefaultValueParams();

  const handleChange = (choice: SingleValue<Choice>) => {
    const newValue = choice?.value as string;

    if (newValue === rangeParam) {
      return;
    }
    searchParams.set(range, newValue as string);
    setKpiRange(range, newValue as Range);
    setSearchParams(searchParams);
    // tracking changed
    eventTrack({
      eventName: "interaction_metric",
      actionName: "filter applied",
      key1: "metric_name",
      value1: title,
      key2: "filter_name",
      value2: newValue,
    });
  };

  const totalControledItemCount = safeCount(deavaData?.totalControledItemCount);
  const toControlItemCount = safeCount(deavaData?.toControlItemCount);
  const totalInitializedItemCount = safeCount(deavaData?.totalInitializedItemCount);
  const toInitializeItemCount = safeCount(deavaData?.toInitializeItemCount);

  const controlPourcentage = computePercentage(
    totalControledItemCount,
    toControlItemCount
  );
  const initializePourcentage = computePercentage(
    totalInitializedItemCount,
    toInitializeItemCount
  );
  
  const {t} = useTranslation();

  return (
    <BaseDashboardCard
      hasModal={true}
      title={title}
      size={"big"}
      option={
        <SelectBox py={1}>
          <SelectOption
            list={options}
            value={rangeParam}
            handleChange={handleChange}
          />
        </SelectBox>
      }
      importedDateTime={deavaData?.importedDateTime}
      {...res}
    >
      <BoxCustom
        gap="1.5rem"
        justifyContent={"center"}
        alignItems={"center"}
        flexDirection={"column"}
      >
        <BoxCustom
          gap={`0.8rem`}
          flexDirection={"column"}
          alignItems={"center"}
          width="100%"
          borderRadius={1}
          pt={[1, 4]}
        >
          <Box display={"flex"} justifyContent={"space-between"} width={"100%"}>
            <Box
              borderRadius={1}
              border={"1px solid #BDC7DA"}
              display={"flex"}
              flexDirection={"column"}
              justifyContent={"flex-start"}
              alignItems={"flex-start"}
              py={1}
              px={2}
              width={"47%"}
            >
              <Box
                display={"flex"}
                alignItems={"center"}
                pt={1}
                textAlign={"left"}
              >
                <Text size={"m"} tagStyle="p">
                  {t('operationnel.tx_produits_init')}
                </Text>
              </Box>
              <Box display={"flex"} alignItems={"baseline"} pb={1}>
                <Text
                  color={getColorText(initializePourcentage)}
                  size={"m"}
                  tagStyle="h1"
                >{formatPercentage(
                  initializePourcentage,
                  "")}</Text>
                <Text
                  color={getColorText(initializePourcentage)}
                  tagStyle="p"
                >{`%`}</Text>
              </Box>
              <Box display={"flex"} alignItems={"baseline"}>
                <Text
                  color="#575757"
                  size={"m"}
                  tagStyle="p"
                >{`${totalInitializedItemCount}/${toInitializeItemCount}`}</Text>
              </Box>
            </Box>
            <Box
              borderRadius={1}
              border={"1px solid #BDC7DA"}
              display={"flex"}
              flexDirection={"column"}
              alignItems={"flex-start"}
              py={1}
              px={2}
              width={"47%"}
            >
              <Box
                display={"flex"}
                alignItems={"center"}
                pt={1}
                pb={isBreak === "phone" ? "27px" : 0}
              >
                <Text size={"s"} tagStyle="p">
                  {t('operationnel.tx_control')}
                </Text>
              </Box>
              <Box display={"flex"} alignItems={"baseline"} pb={1}>
                <Text
                  color={getColorText(controlPourcentage)}
                  size={"m"}
                  tagStyle="h1"
                >{formatPercentage(
                  controlPourcentage,
                  "")}</Text>
                <Text
                  color={getColorText(controlPourcentage)}
                  tagStyle="p"
                >{`%`}</Text>
              </Box>
              <Box display={"flex"} alignItems={"baseline"}>
                <Text
                  color="#575757"
                  size={"m"}
                  tagStyle="p"
                >{`${totalControledItemCount}/${toControlItemCount}`}</Text>
              </Box>
            </Box>
          </Box>
        </BoxCustom>
      </BoxCustom>
    </BaseDashboardCard>
  );
};

export default DeavaCard;
